import React, { useState, useEffect, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { isAngebot, isBestellung } from '@/lib/orderFields';
import { upsertCustomerPricesFromItems } from '@/lib/customerPriceApi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText, ShoppingCart, Plus, Package, ChevronRight, Building2, Eye, Download, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import StatusBadge from '@/components/ui/StatusBadge';
import OrderForm from '@/components/orders/OrderForm';
import { downloadOrderPDF } from '@/components/orders/OrderPDFGenerator';
import { updateLeadScore } from '@/components/utils/LeadScoreUpdater';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useAuth } from '@/api/AuthContext';
import { normalizeLeadStatus } from '@/lib/leadStatus';
import { pushOrderInBackground } from '@/lib/shopifyOrderPush';
import {
  reserveOrderItems,
  releaseStock,
  StockApiError,
  stockErrorToGerman,
} from '@/lib/stockApi';

export default function AgentOrders() {
  const { user } = useAuth();
  const [filter, setFilter] = useState('all');
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [orderType, setOrderType] = useState('Bestellung');
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectLeadOpen, setSelectLeadOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [leadSearchTerm, setLeadSearchTerm] = useState('');
  const queryClient = useQueryClient();

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['agent-orders', user?.email],
    queryFn: () => base44.entities.Order.filter({ agent_email: user?.email }, '-created_date'),
    enabled: !!user?.email,
    staleTime: 1000 * 60,
  });

  const { data: leads = [] } = useQuery({
    queryKey: ['agent-leads-for-orders', user?.email],
    queryFn: () => base44.entities.Lead.filter({ assigned_agent: user?.email }, 'name'),
    enabled: !!user?.email,
    staleTime: 1000 * 60 * 5,
  });

  // Firma arama sonuçları — tek yerde hesaplanır (Kontakt auswählen sheet'i)
  const filteredLeads = useMemo(() => {
    if (!leadSearchTerm) return leads;
    const term = leadSearchTerm.toLowerCase();
    return leads.filter((l) =>
      l.name?.toLowerCase().includes(term) ||
      l.branche?.toLowerCase().includes(term) ||
      l.ort?.toLowerCase().includes(term) ||
      l.plz?.toLowerCase().includes(term)
    );
  }, [leads, leadSearchTerm]);

  const createOrderMutation = useMutation({
    mutationFn: async (data) => {
      // Create order
      const order = await base44.entities.Order.create({
        ...data,
        agent_email: user?.email,
        order_status: orderType === 'Angebot' ? 'pending_review' : 'completed'
      });

      // Yan etki hataları — sipariş zaten oluşturuldu, bu yüzden bunları
      // kullanıcıya uyarı olarak gösterip siparişin kendisini iade ediyoruz.
      const sideEffectWarnings = [];

      // Stok rezervasyonu — sadece Bestellung tipinde (Angebot'lar rezerve
      // etmez çünkü henüz onaylanmış satış değil). Sayım henüz
      // tamamlanmadığı için allowNegative=true (backorder'a izin verir);
      // sayım tamamlandığında bu false'a çevrilir.
      if (orderType === 'Bestellung' && Array.isArray(data.items)) {
        try {
          const result = await reserveOrderItems({
            items: data.items.map((it) => ({
              productId: it.product_id,
              variantValue: it.variant_value ?? it.variant ?? null,
              quantity: Number(it.quantity) || 0,
            })),
            orderId: order.id,
            actorEmail: user?.email,
            allowNegative: true,
          });
          if (result?.skipped) {
            sideEffectWarnings.push('Stok motoru devre dışı (Supabase bağlantısı yok)');
          }
        } catch (err) {
          // Rezervasyon başarısız. reserveOrderItems içinde zaten önceki
          // rezervler release edildi. Siparişi de silip temiz bırakıyoruz.
          try {
            await base44.entities.Order.delete(order.id);
          } catch (deleteErr) {
            console.error('Order rollback failed:', deleteErr);
          }
          if (err instanceof StockApiError) {
            throw new Error(stockErrorToGerman(err));
          }
          throw err;
        }
      }

      // Bestellung für den Versand an Shopify senden (Hintergrund, idempotent)
      if (orderType === 'Bestellung') {
        pushOrderInBackground(order.id, { orderNumber: order.order_number });
      }

      // Update lead status: Potenzieller Interessent → Interessenten when offer sent
      if (orderType === 'Angebot' && data.lead_id) {
        try {
          const leadData = leads.find(l => l.id === data.lead_id);
          if (leadData && normalizeLeadStatus(leadData.status) === 'Potenzieller Interessent') {
            await base44.entities.Lead.update(data.lead_id, {
              status: 'Interessenten'
            });
            queryClient.invalidateQueries(['agent-leads']);
          }
        } catch (e) {
          sideEffectWarnings.push('Kontakt-Status konnte nicht aktualisiert werden');
        }
      }

      // Auto-save custom prices to CustomerPrice manager
      if (data.items && data.items.length > 0 && data.lead_id) {
        const lD = leads.find(l => l.id === data.lead_id);
        const { errors } = await upsertCustomerPricesFromItems({
          leadId: data.lead_id,
          leadName: lD ? lD.name : (data.lead_name || ''),
          items: data.items,
        });
        queryClient.invalidateQueries(['customer-prices']);
        if (errors.length > 0) {
          sideEffectWarnings.push('Kundenpreise konnten nicht gespeichert werden');
        }
      }

      // Başarı mesajı — yan etki uyarılarıyla birlikte
      if (sideEffectWarnings.length > 0) {
        const baseMsg = orderType === 'Angebot'
          ? 'Angebot erstellt, aber:'
          : 'Bestellung erstellt, aber:';
        toast.warning(`${baseMsg} ${sideEffectWarnings.join('; ')}`);
      } else if (orderType === 'Angebot') {
        toast.success('Angebot erstellt und an Admin zur Überprüfung gesendet');
      } else {
        toast.success('Bestellung erstellt');
      }

      return order;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['agent-orders']);
      // DB trigger'ı Bestellung sonrası kontağı otomatik 'Kunde' yapar —
      // listelerin terfiyi hemen göstermesi için lead cache'leri tazelenir.
      queryClient.invalidateQueries(['agent-leads']);
      queryClient.invalidateQueries(['leads']);
      queryClient.invalidateQueries(['admin-leads']);
      setShowNewOrder(false);
      setSelectedLead(null);
    },
    onError: (error) => {
      console.error('Order creation error:', error);
      toast.error('Fehler beim Erstellen: ' + error.message);
    }
  });

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    return filter === 'Angebot' ? isAngebot(order) : isBestellung(order);
  });

  const startNewOrder = (type) => {
    setOrderType(type);
    setSelectLeadOpen(true);
  };

  const handleLeadSelect = (lead) => {
    setSelectedLead(lead);
    setSelectLeadOpen(false);
    setShowNewOrder(true);
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setDetailOpen(true);
  };

  const handleDownloadPDF = async (order) => {
    try {
      await downloadOrderPDF(order);
    } catch (e) {
      toast.error('PDF konnte nicht erstellt werden');
    }
  };

  const angeboteCount = orders.filter(isAngebot).length;
  const bestellungenCount = orders.filter(isBestellung).length;

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-brand-950 flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight text-slate-900">Aufträge</h1>
                <p className="text-sm text-slate-500">{orders.length} gesamt</p>
              </div>
            </div>
          </div>

          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="w-full grid grid-cols-3 bg-slate-100">
              <TabsTrigger value="all" className="text-xs sm:text-sm">
                Alle ({orders.length})
              </TabsTrigger>
              <TabsTrigger value="Angebot" className="text-xs sm:text-sm">
                Angebote ({angeboteCount})
              </TabsTrigger>
              <TabsTrigger value="Bestellung" className="text-xs sm:text-sm">
                Bestell. ({bestellungenCount})
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Order List */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {isLoading ? (
          <div className="space-y-3 md:space-y-0 md:grid md:grid-cols-2 xl:grid-cols-3 md:gap-3">
            {[1, 2, 3].map(i => (
              <Card key={i} className="p-4">
                <Skeleton className="h-6 w-1/3 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </Card>
            ))}
          </div>
        ) : filteredOrders.length > 0 ? (
          <div className="space-y-3">
            {filteredOrders.map(order => (
              <Card
                key={order.id}
                className={cn(
                  'overflow-hidden border-l-4 cursor-pointer hover:shadow-md transition-shadow',
                  isAngebot(order) ? 'border-l-orange-500' : 'border-l-emerald-500'
                )}
                onClick={() => handleOrderClick(order)}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-mono text-sm text-slate-500">{order.order_number}</p>
                      <h3 className="font-semibold text-slate-900">{order.lead_name}</h3>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <StatusBadge status={order.order_type} type="order" />
                      {order.order_status === 'pending_review' && (
                        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                          Wird geprüft
                        </span>
                      )}
                      {order.order_status === 'approved' && (
                        <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                          Genehmigt
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">
                      {order.created_date && format(new Date(order.created_date), 'dd. MMM yyyy', { locale: de })}
                    </span>
                    <span className="text-slate-500">
                      {order.items?.length || 0} Positionen
                    </span>
                  </div>

                  <div className="mt-3 pt-3 border-t flex items-center justify-between">
                    <span className="text-sm text-slate-500">Gesamt</span>
                    <span className="font-bold text-xl">€{(order.total || 0).toFixed(2)}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-8 text-center">
            <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-600 font-medium">Keine Aufträge</p>
            <p className="text-sm text-slate-400">
              Erstellen Sie Ihr erstes Angebot oder Bestellung
            </p>
          </Card>
        )}
      </div>

      {/* Floating Action Buttons */}
      <div className="fixed bottom-20 right-4 flex flex-col gap-3 z-20">
        <Button
          onClick={() => startNewOrder('Angebot')}
          className="h-14 w-14 rounded-full bg-orange-500 hover:bg-orange-600 shadow-lg"
          title="Angebot erstellen"
          aria-label="Angebot erstellen"
        >
          <FileText className="w-6 h-6" />
        </Button>
        <Button
          onClick={() => startNewOrder('Bestellung')}
          className="h-14 w-14 rounded-full bg-emerald-500 hover:bg-emerald-600 shadow-lg"
          title="Bestellung erstellen"
          aria-label="Bestellung erstellen"
        >
          <ShoppingCart className="w-6 h-6" />
        </Button>
      </div>

      {/* Lead Selection Sheet */}
      <Sheet open={selectLeadOpen} onOpenChange={(open) => {
        setSelectLeadOpen(open);
        if (!open) setLeadSearchTerm('');
      }}>
        {/* h-[85dvh]: dvh mobil klavye/adres çubuğunda beyaz alan bırakmaz */}
        <SheetContent side="bottom" className="h-[85dvh] flex flex-col rounded-t-2xl">
          <SheetHeader className="mb-2 flex-shrink-0">
            <SheetTitle>Kontakt auswählen</SheetTitle>
          </SheetHeader>

          {/* Search Bar */}
          <div className="relative mb-3 flex-shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Kunde suchen (Name, Branche, Ort)..."
              value={leadSearchTerm}
              onChange={(e) => setLeadSearchTerm(e.target.value)}
              className="pl-9 pr-9 h-11 text-base"
              autoFocus
            />
            {leadSearchTerm && (
              <button
                onClick={() => setLeadSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            )}
          </div>

          {/* Filtered Results Count */}
          {leadSearchTerm && (
            <p className="text-xs text-slate-400 mb-2 flex-shrink-0">
              {filteredLeads.length} Ergebnis{filteredLeads.length !== 1 ? 'se' : ''}
            </p>
          )}

          <div className="space-y-2 overflow-y-auto flex-1 overscroll-contain pb-[env(safe-area-inset-bottom)]">
            {filteredLeads.map(lead => (
              <button
                key={lead.id}
                onClick={() => handleLeadSelect(lead)}
                className="w-full p-4 rounded-lg border hover:bg-slate-50 active:bg-slate-100 transition-colors flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-slate-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{lead.name}</p>
                    <p className="text-sm text-slate-500 truncate">
                      {[lead.branche, lead.ort].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
              </button>
            ))}
            {leadSearchTerm && filteredLeads.length === 0 && (
              <div className="text-center py-8">
                <Building2 className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm text-slate-400">Kein Kontakt gefunden</p>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Order Form Sheet */}
      <Sheet open={showNewOrder} onOpenChange={setShowNewOrder}>
        <SheetContent side="bottom" className="h-[92dvh] overflow-y-auto rounded-t-2xl overscroll-contain">
          {selectedLead && (
            <OrderForm
              lead={selectedLead}
              orderType={orderType}
              onSubmit={async (data) => {
                try {
                  await createOrderMutation.mutateAsync(data);
                } catch (error) {
                  // Error handled in mutation
                }
              }}
              onCancel={() => {
                setShowNewOrder(false);
                setSelectedLead(null);
              }}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Order Detail Sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          {selectedOrder && (
            <div className="space-y-4">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  {isAngebot(selectedOrder) ? (
                    <FileText className="w-5 h-5 text-orange-600" />
                  ) : (
                    <ShoppingCart className="w-5 h-5 text-emerald-600" />
                  )}
                  {selectedOrder.order_number}
                </SheetTitle>
              </SheetHeader>

              {/* Order Info */}
              <Card className="p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Kunde</span>
                  <span className="font-medium">{selectedOrder.lead_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Typ</span>
                  <StatusBadge status={selectedOrder.order_type} type="order" />
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Datum</span>
                  <span className="text-sm">
                    {selectedOrder.created_date && format(new Date(selectedOrder.created_date), 'dd. MMMM yyyy', { locale: de })}
                  </span>
                </div>
                {selectedOrder.order_status && (
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">Status</span>
                    <span className={cn(
                      'text-xs px-2 py-0.5 rounded-full',
                      selectedOrder.order_status === 'pending_review' && 'text-amber-700 bg-amber-50',
                      selectedOrder.order_status === 'approved' && 'text-green-700 bg-green-50',
                      selectedOrder.order_status === 'completed' && 'text-blue-700 bg-blue-50',
                    )}>
                      {selectedOrder.order_status === 'pending_review' ? 'Wird geprüft' :
                        selectedOrder.order_status === 'approved' ? 'Genehmigt' :
                          selectedOrder.order_status === 'completed' ? 'Abgeschlossen' :
                            selectedOrder.order_status}
                    </span>
                  </div>
                )}
              </Card>

              {/* Items */}
              <Card>
                <div className="p-4 border-b">
                  <h3 className="font-semibold text-sm">Positionen ({selectedOrder.items?.length || 0})</h3>
                </div>
                <div className="divide-y">
                  {selectedOrder.items?.map((item, i) => (
                    <div key={i} className="p-3 space-y-1.5">
                      {/* Product name — full width, no truncation */}
                      <p className="font-medium text-sm text-slate-900 leading-snug break-words">{item.product_name}</p>
                      {item.product_sku && (
                        <p className="text-[11px] text-slate-400 font-mono">{item.product_sku}</p>
                      )}
                      {/* Description if available */}
                      {(item.beschreibung || item.description) && (
                        <p className="text-xs text-slate-500 leading-relaxed">{item.beschreibung || item.description}</p>
                      )}
                      {/* Quantity × Price on its own row */}
                      <div className="flex items-center justify-between pt-1.5 border-t border-slate-100">
                        <span className="text-sm text-slate-500">{item.quantity} × €{(item.unit_price || 0).toFixed(2)}</span>
                        <span className="font-semibold text-sm">€{((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Totals */}
              <Card className="p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Zwischensumme</span>
                  <span>€{(selectedOrder.subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">MwSt. ({selectedOrder.tax_rate || 19}%)</span>
                  <span>€{(selectedOrder.tax_amount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Gesamt</span>
                  <span className={isAngebot(selectedOrder) ? 'text-orange-600' : 'text-emerald-600'}>
                    €{(selectedOrder.total || 0).toFixed(2)}
                  </span>
                </div>
              </Card>

              {/* Notes */}
              {selectedOrder.notes && (
                <Card className="p-4">
                  <h3 className="font-semibold text-sm text-slate-500 mb-1">Notizen</h3>
                  <p className="text-sm text-slate-700">{selectedOrder.notes}</p>
                </Card>
              )}

              {/* Actions */}
              <Button
                onClick={() => handleDownloadPDF(selectedOrder)}
                className="w-full gap-2"
                variant="outline"
              >
                <Download className="w-4 h-4" />
                PDF herunterladen
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
